// ═══════════════════════════════════════════════════════════════════
//  modules/erp/PlayerInventoryTab.js
//  PlayerInventoryTab
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { EnderchestGrid } from "./EnderchestGrid";
import { InvActionModal } from "./InvActionModal";
import { MinecraftInventory } from "./MinecraftInventory";
import { SkinRender } from "./SkinRender";

export function PlayerInventoryTab({ uuid, username, token, canAct }) {
  const [invData, setInvData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sub, setSub] = useState("inv");      // inv | ender
  const [action, setAction] = useState(null); // { container, slot, item }

  const loadInventory = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/players/${uuid}/inventory`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Erreur"); } else { setInvData(data); }
    } catch { setError("Impossible de contacter le serveur"); }
    setLoading(false);
  }, [token, uuid]);

  useEffect(() => { loadInventory(); }, [uuid, loadInventory]);

  const onSlotClick = (container, slot, item) => { if (canAct) setAction({ container, slot, item }); };

  const sourceLabel = invData && (
    invData.source === "live" ? { txt: "🟢 En direct (RCON)", bg: THEME.emeraldDim, col: THEME.emerald }
    : invData.source === "snapshot_fallback" ? { txt: "🟠 Snapshot (RCON indispo)", bg: THEME.amberDim, col: THEME.amber }
    : { txt: "💾 Snapshot", bg: THEME.surface, col: THEME.textMuted }
  );

  const subBtn = (id, label) => (
    <button onClick={() => setSub(id)} style={{
      background: sub === id ? THEME.panel : "transparent",
      border: `1px solid ${sub === id ? THEME.border : "transparent"}`,
      color: sub === id ? THEME.text : THEME.textMuted,
      borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: sub === id ? 700 : 400 }}>{label}</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Bandeau statut */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {invData && (
            <Badge text={invData.online_server ? `🟢 En ligne (${invData.online_server})` : "⚫ Hors ligne"}
              bg={invData.online_server ? THEME.emeraldDim : THEME.surface} color={invData.online_server ? THEME.emerald : THEME.textMuted} />
          )}
          {sourceLabel && <Badge text={sourceLabel.txt} bg={sourceLabel.bg} color={sourceLabel.col} />}
          {invData && invData.updated_at && (
            <span style={{ color: THEME.textDim, fontSize: 11 }}>màj {new Date(invData.updated_at).toLocaleString("fr")}</span>
          )}
        </div>
        <button onClick={loadInventory} disabled={loading}
          style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "7px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, opacity: loading ? 0.6 : 1 }}>
          {loading ? "Chargement..." : "🔄 Rafraîchir"}
        </button>
      </div>

      {/* Stats */}
      {invData && invData.stats && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Badge text={`❤️ ${invData.stats.health ?? "?"}`} bg={THEME.redDim} color={THEME.red} />
          <Badge text={`🍗 ${invData.stats.food ?? "?"}`} bg={THEME.amberDim} color={THEME.amber} />
          <Badge text={`⭐ niv. ${invData.stats.xp_level ?? "?"}`} bg={THEME.purpleDim} color={THEME.purple} />
        </div>
      )}

      {error && <div style={{ color: THEME.red, fontSize: 13, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 6, padding: "8px 12px" }}>{error}</div>}

      {canAct && (
        <div style={{ color: THEME.gold, fontSize: 12 }}>🔑 Clique un item pour le retirer (double confirmation requise).</div>
      )}

      {/* Sous-onglets inv / ender */}
      <div style={{ display: "flex", gap: 4 }}>
        {subBtn("inv", "🎒 Inventaire")}
        {subBtn("ender", "🟪 Enderchest")}
      </div>

      {invData && sub === "inv" && (
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 600 }}>SKIN</div>
            <SkinRender uuid={uuid} username={username} />
            <div style={{ color: THEME.text, fontSize: 12, fontWeight: 600 }}>{username}</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <MinecraftInventory items={invData.main || []} canAct={canAct} onSlotClick={onSlotClick} />
          </div>
        </div>
      )}

      {invData && sub === "ender" && (
        <div style={{ overflowX: "auto" }}>
          {(!invData.enderReady && !invData.online_server) ? (
            <div style={{ color: THEME.amber, fontSize: 13, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 14px" }}>
              ⏳ Le coffre de l'Ender hors-ligne sera disponible une fois le plugin mis à jour (colonne ender_data).
            </div>
          ) : (
            <EnderchestGrid items={invData.ender || []} canAct={canAct} onSlotClick={onSlotClick} />
          )}
        </div>
      )}

      {!invData && !loading && !error && (
        <div style={{ color: THEME.textDim, fontSize: 13, textAlign: "center", padding: 40 }}>Aucune donnée d'inventaire.</div>
      )}

      {action && (
        <InvActionModal uuid={uuid} token={token} target={action}
          onClose={() => setAction(null)} onDone={loadInventory} />
      )}
    </div>
  );
}
